import { isOwnMessageFromStream, updateMessage } from "@/helpers/helpers";
import { scrollToTheBottom } from "@/helpers/DOMHelper";
import { CHAT_MODES, CONTENT_TYPE_SLUGS } from "@/helpers/constants";

/**
 * DigitalEvent Mutations
 */
import Vue from "vue";

export default {
  /**
   * Set default state
   * @param state {Object}
   */
  setDefaultState(state) {
    state.locks = {};
  },

  /**
   * Restrict Access
   * @param state {Object}
   */
  restrictAccess(state) {
    clearInterval(state.socket.pingInterval);
    clearInterval(state.socket.statePullingInterval);
    if (state.socket.socket) {
      state.socket.socket.disconnect();
    }

    // TODO: create separate action and mutation with Object.assign(state, ...) to reset state
    state.room.roomAssignments = [];
    state.room.updateCounter = 0;
    state.room.deState = [];
    state.room.deStateUpdatedAt = null;
    state.socket.socket = null;
    state.socket.pingInterval = null;
    state.socket.statePullingInterval = null;
    state.socket.announcements = [];
    state.socket.announce = null;
    state.chat.roomMessages = [];
    state.chat.tableMessages = [];
    state.chat.unreadMessagesCounter = {
      TABLE: 0,
      PRIVATE: 0
    };

    state.chat.chatMode = "TABLE";
    state.chat.privateMessages = [];
    state.chat.privateChatTarget = null;
    state.chat.privateChatTargetUsers = [];
    state.users.userConnections = [];
    state.chat.privateChatFilters = {
      NETWORK: true,
      OTHER: true
    };

    state.videoChat.mediaAccessError = null;
    state.videoChat.videoChats = [];
    state.videoChat.videoChatMembers = [];
    state.videoChat.oneToOneChatParticipantFullName = "";
    state.videoChat.activeVideoChat = null;
    state.videoChat.activeVideoSession = null;
    state.videoChat.subscribers = [];
    state.videoChat.streams = [];
    state.videoChat.connectionsCount = 0;

    state.invites.videoCallInviteCreationStatus = null;
    state.invites.receivedInvites = [];
    state.invites.sentInvites = [];
    state.invites.sentInvitesUsersMap = {
      TABLE_MOVEMENT: {},
      VIDEO_CALL: {}
    };
    state.invites.displayingReceivedInvites = {};

    state.locks = {};
  },

  /**
   * Set useFullPagePreloader flag
   * @param state
   * @param value
   */
  setPreloaderFlag(state, value) {
    state.useFullPagePreloader = value;
  },

  /**
   * Set theme styles
   * @param state {Object}
   * @param themeStyles {Object}
   */
  setThemeStyles(state, themeStyles) {
    state.themeStyles = themeStyles;
  },

  /**
   * Set Lock to the object
   * @param state {Object}
   * @param name {String}
   * @param lock {Boolean}
   */
  setLock(state, { name, lock }) {
    Vue.set(state.locks, name, lock);
  },

  /**
   * Add message to the roomMessages list
   * @param state {Object}
   * @param groupUUID {String}
   * @param message {Object}
   */
  addToRoomMessages(state, { groupUUID, message }) {
    // TODO: temp fix, need to figure out and standardize time across messages
    message.createdAt = new Date().toISOString();
    if (message.createdAt && !message.createdAt.endsWith("Z")) {
      message.createdAt += "Z";
    }

    if (isOwnMessageFromStream(message, state.users.user.user_uuid)) {
      updateMessage(message, state.chat.roomMessages);
    } else {
      state.chat.roomMessages.push(message);
      scrollToTheBottom(`group-${groupUUID}-messages`);
    }
  },

  /**
   * Add message to the tableMessages list
   * @param state {Object}
   * @param groupUUID {String}
   * @param message {Object}
   */
  addToTableMessages(state, { groupUUID, message }) {
    // TODO: temp fix, need to figure out and standardize time across messages
    message.createdAt = new Date().toISOString();
    if (message.createdAt && !message.createdAt.endsWith("Z")) {
      message.createdAt += "Z";
    }

    if (isOwnMessageFromStream(message, state.users.user.user_uuid)) {
      updateMessage(message, state.chat.tableMessages);
    } else {
      state.chat.tableMessages.push(message);
      scrollToTheBottom(`group-${groupUUID}-messages`);
    }

    if (state.chat.chatMode !== CHAT_MODES.TABLE) {
      state.chat.unreadMessagesCounter.TABLE++;
    }
  },

  /**
   * Add private message to the store
   * @param state {Object}
   * @param message {Object}
   */
  addToPrivateMessages(state, { message }) {
    // TODO: temp fix, need to figure out and standardize time across messages
    message.createdAt = new Date().toISOString();
    if (message.createdAt && !message.createdAt.endsWith("Z")) {
      message.createdAt += "Z";
    }
    state.chat.privateMessages.push(message);
    scrollToTheBottom("privateMessages");

    if (
      state.chat.chatMode !== CHAT_MODES.PRIVATE ||
      (state.chat.privateChatTarget &&
        state.chat.privateChatTarget.userUUID !== message.userUUID &&
        state.users.user.user_uuid !== message.userUUID)
    ) {
      state.chat.unreadMessagesCounter.PRIVATE++;
    }
  },
  //TODO: Rebuild this logic from global to local module
  /**
   * Add new table to the Room
   * @param state {Object}
   * @param table {Object} ContentNode context
   * @param activeRoom {Object} ContentNode context
   */
  addTableToRoom(state, { table, activeRoom = null }) {
    let room =
      activeRoom ||
      state.event.context.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_ROOM].find(
        event_room => event_room.SLUG === state.room.activeRoom
      );

    if (Object.prototype.hasOwnProperty.call(room.children, CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE)) {
      if (!room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE].length) {
        room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE] = [table];
      } else if (
        !room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE].some(
          room_table => room_table.TABLE_ID === table.TABLE_ID
        )
      ) {
        room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE].splice(
          room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE].length,
          0,
          table
        );
      }
    } else {
      room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE] = [table];
    }
  },

  //TODO: Rebuild this logic from global to local module
  /**
   * Update table info
   * @param state {Object}
   * @param tableUUID {String} ContentNode.uuid (DigitalEventTable CT)
   * @param data {Object} ContentNode context
   * @param activeRoom {Object} ContentNode context
   */
  updateTable(state, { tableUUID, data, activeRoom = null }) {
    let room =
      activeRoom ||
      state.event.context.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_ROOM].find(
        event_room => event_room.SLUG === state.room.activeRoom
      );
    let tables = room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE];
    let table = tables.find(t => t.uuid === tableUUID);
    Object.assign(table, data);
  },

  //TODO: Rebuild this logic from global to local module
  /**
   * Delete table from the room
   * @param state {Object}
   * @param tableUUID {String} ContentNode.uuid (DigitalEventTable CT)
   */
  deleteTable(state, { tableUUID }) {
    let room = state.event.context.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_ROOM].find(
      event_room => event_room.SLUG === state.room.activeRoom
    );
    let tables = room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE];

    let tableIndex = tables.findIndex(t => t.uuid === tableUUID);
    room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE].splice(tableIndex, 1);
  },

  /**
   * Update DigitalEventRoom info in vuex state
   * @param state
   * @param {String} roomUUID
   * @param {Object} data
   * @param activeRoom
   */
  updateDigitalEventRoomInfo(state, { roomUUID, data, activeRoom }) {
    let room = state.event.context.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_ROOM].find(room => {
      return room.uuid === roomUUID;
    });
    if (activeRoom.uuid === data.uuid) {
      state.room.activeRoom = data.SLUG;
    }
    data.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE] = room.children[CONTENT_TYPE_SLUGS.DIGITAL_EVENT_TABLE];

    Object.assign(room, data);
  }
};
